import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle2, Code, RefreshCw } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useProviderCustomScriptsSettings,
  useUpdateProviderCustomScriptsSettings,
} from '@/hooks/provider/useProviderCustomScriptsSettings';
import {
  normalizeProviderCustomScripts,
  ProviderCustomScriptsRequestError,
} from '@/services/provider/custom-scripts.service';

type FormState = {
  customScripts: string;
};

export function ProviderCustomScriptsCard() {
  const { t } = useTranslation();
  const settingsQuery = useProviderCustomScriptsSettings();
  const updateMutation = useUpdateProviderCustomScriptsSettings();

  const schema = useMemo(
    () =>
      z.object({
        customScripts: z.string().optional(),
      }),
    [],
  );

  const form = useForm<FormState>({
    resolver: zodResolver(schema),
    defaultValues: {
      customScripts: '',
    },
  });

  useEffect(() => {
    if (settingsQuery.data) {
      form.reset({ customScripts: settingsQuery.data.customScripts ?? '' });
    }
  }, [form, settingsQuery.data]);

  const isSaving = updateMutation.isPending;

  const handleSubmit = async (values: FormState) => {
    const normalized = normalizeProviderCustomScripts(values.customScripts);
    try {
      const updated = await updateMutation.mutateAsync({ customScripts: normalized });
      form.reset({ customScripts: updated.customScripts ?? '' });
      toast.success(
        t('settings.customScripts.messages.saveSuccess', 'Custom scripts saved.'),
      );
    } catch (error) {
      form.clearErrors('customScripts');
      if (error instanceof ProviderCustomScriptsRequestError && error.errors?.custom_scripts?.[0]) {
        form.setError('customScripts', {
          type: 'server',
          message: error.errors.custom_scripts[0],
        });
      }
      const message =
        error instanceof Error && error.message
          ? error.message
          : t('settings.customScripts.messages.saveError', 'Unable to save custom scripts.');
      toast.error(message);
    }
  };

  if (settingsQuery.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="h-5 w-5 text-muted-foreground" />
            {t('settings.customScripts.title', 'Custom scripts')}
          </CardTitle>
          <CardDescription>
            {t(
              'settings.customScripts.description',
              'Inject custom HTML or scripts into every customer and provider page.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-10 w-32" />
        </CardContent>
      </Card>
    );
  }

  if (settingsQuery.isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            {t('settings.customScripts.title', 'Custom scripts')}
          </CardTitle>
          <CardDescription>
            {t(
              'settings.customScripts.description',
              'Inject custom HTML or scripts into every customer and provider page.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertTitle>
              {t('settings.customScripts.messages.loadError', 'Unable to load custom scripts.')}
            </AlertTitle>
            <AlertDescription className="mt-2 flex items-center gap-3">
              <span>{settingsQuery.error.message}</span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => settingsQuery.refetch()}
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                {t('common.retry', 'Retry')}
              </Button>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Code className="h-5 w-5 text-muted-foreground" />
          {t('settings.customScripts.title', 'Custom scripts')}
        </CardTitle>
        <CardDescription>
          {t(
            'settings.customScripts.description',
            'Inject custom HTML or scripts into every customer and provider page.',
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form className="space-y-4" onSubmit={form.handleSubmit(handleSubmit)}>
            <FormField
              control={form.control}
              name="customScripts"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('settings.customScripts.fields.script', 'Custom scripts')}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      rows={6}
                      placeholder={t(
                        'settings.customScripts.placeholders.script',
                        "<script>console.log('custom')</script>",
                      )}
                      className="font-mono text-sm"
                    />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    {t(
                      'settings.customScripts.helper',
                      'Injected into the page head without sanitization. Leave blank to remove.',
                    )}
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => form.reset({ customScripts: '' })}
                disabled={isSaving}
              >
                {t('common.clear', 'Clear')}
              </Button>
              <Button type="submit" disabled={isSaving} className="gap-2">
                {isSaving && <CheckCircle2 className="h-4 w-4 animate-spin" />}
                {t('settings.customScripts.actions.save', 'Save custom scripts')}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
